import { makeAutoObservable } from 'mobx';
import { isValidEmail } from 'hevy-shared';
import { stores } from 'state/stores';
import { fetchUserContentAfterLogin } from 'utils/fetchUserContent';
import { SocialLoginResponse } from 'utils/API';
import { log } from 'utils/log';
import { sendEvent } from 'utils/events';
import axios from 'axios';
import { recaptcha } from './utils';
import { CodeResponse } from '@react-oauth/google';
import router from 'next/router';
import { themeState } from 'styleguide/theme';

interface SignupViewModelProps {
  afterSignup?: (isNewUser: boolean) => Promise<void>;
}

export class SignupViewModel {
  email: string = '';
  password: string = '';
  errorMessage?: string;
  emailErrorStatus?: string;
  passwordErrorStatus?: string;
  gympassUserId?: string;
  isLoading: boolean = false;
  afterSignup?: (isNewUser: boolean) => Promise<void>;

  constructor(props?: SignupViewModelProps) {
    this.afterSignup = props?.afterSignup;
    makeAutoObservable(this);

    log('SignupViewModel constructor');
    sendEvent('signup_screenview');
  }

  /**
   * set error messages and highlight fields with invalid input
   * @param errorMessage error message to display in red text at the bottom of the form
   * @param emailErrorStatus error message to display next to the email field
   * @param passwordErrorStatus error message to display next to the password field
   */
  setError = (
    errorMessage: string | undefined,
    emailErrorStatus?: string,
    passwordErrorStatus?: string,
  ) => {
    this.errorMessage = errorMessage;
    this.emailErrorStatus = emailErrorStatus;
    this.passwordErrorStatus = passwordErrorStatus;
  };

  clearError = () => {
    this.setError(undefined);
  };

  onUpdateEmail = (value: string) => {
    this.email = value;
    this.emailErrorStatus = undefined;
  };

  onUpdatePassword = (value: string) => {
    this.password = value;
    this.passwordErrorStatus = undefined;
  };

  get isValidEmail() {
    return isValidEmail(this.email);
  }

  get isValidPassword() {
    return this.password.length > 5;
  }

  get isSignupButtonEnabled() {
    return this.email.length > 0 && this.password.length > 0;
  }

  onSignup = async () => {
    if (!this.isSignupButtonEnabled) return;

    themeState.push('light');
    try {
      await this.onSignupPress();
      router.push('/onboarding/username');
    } catch {
      themeState.pop();
    }
  };

  /**
   * Google Signup
   */

  onSignupWithGoogleSuccess = async (response: CodeResponse) => {
    sendEvent('signup_click', { source: 'google' });

    let loginResult: SocialLoginResponse;

    try {
      loginResult = await stores.auth.signUpWithGoogle(response.code);
    } catch {
      this.onSignupWithGoogleError();
      return;
    }

    sendEvent('signup_complete', { source: 'google' });

    await this.afterSignup?.(loginResult.is_new_user);

    this.fetchUserData();

    if (loginResult.is_new_user) {
      router.push('/onboarding/username');
    } else {
      router.push('/');
    }
  };

  onSignupWithGoogleError = () => {
    this.setError('Failed to sign up with Google.');
  };

  /**
   * Apple Signup
   */

  onSignupWithAppleClick = async () => {
    sendEvent('signup_click', { source: 'apple' });
    this.clearError();

    let loginResult: SocialLoginResponse | null = null;

    try {
      const result = await (window as any).AppleID?.auth?.signIn();
      const identityToken = result.authorization?.id_token;
      const maybeEmail = result.user?.email;
      loginResult = await stores.auth.loginWithApple({
        identityToken,
        email: maybeEmail,
        gympassUserId: this.gympassUserId,
      });
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.data.error === 'popup_closed_by_user') return;

      this.setError('Failed to sign up with Apple.');
      return;
    }

    sendEvent('signup_complete', { source: 'apple' });

    await this.afterSignup?.(loginResult.is_new_user);

    this.fetchUserData();

    if (!loginResult) return;

    this.onSignup();
  };

  /**
   * Email Signup
   */

  onSignupPress = async () => {
    if (!this.isValidEmail || !this.isValidPassword) {
      this.setError(
        undefined,
        !this.isValidEmail ? 'You must use a valid email address' : undefined,
        !this.isValidPassword ? 'Password must contain at least 6 characters' : undefined,
      );
      throw new Error();
    }

    sendEvent('signup_click', { source: 'email' });
    this.clearError();

    try {
      this.isLoading = true;

      const recaptchaToken = await recaptcha('signup');
      await stores.auth.signup(this.email, this.password, recaptchaToken, this.gympassUserId);

      sendEvent('signup_complete', { source: 'email' });
      await this.afterSignup?.(true);
      this.fetchUserData();
    } catch (e: unknown | import('axios').AxiosError) {
      this.isLoading = false;

      if (axios.isAxiosError(e)) {
        switch (e.response?.data.error) {
          case 'EmailAlreadyExists':
            this.setError('This email address is already being used');
            break;
          default:
            sendEvent('signup_error', { source: 'email', error: e.message });
        }

        throw e;
      }

      this.setError('An unknown error has occurred');
      throw e;
    }
  };

  private fetchUserData = async () => {
    fetchUserContentAfterLogin();
  };
}
